import type { PageProps } from "@types";
import type { GetServerSideProps, GetServerSidePropsContext } from "next";
import type { AbstractIntlMessages } from "next-intl";

export const locales = ["fa", "en"] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = "fa";

export const defaultTimeZone = "Asia/Tehran";

const loadNamespace = async (
  locale: string,
  namespace: string
): Promise<AbstractIntlMessages | null> => {
  try {
    return (await import(`../locales/${locale}/${namespace}.json`))
      .default as AbstractIntlMessages;
  } catch {
    if (locale !== defaultLocale) {
      try {
        return (await import(`../locales/${defaultLocale}/${namespace}.json`))
          .default as AbstractIntlMessages;
      } catch {
        return null;
      }
    }
    return null;
  }
};

export const getMessages = async (
  locale: string | undefined,
  namespaces?: string | string[]
): Promise<AbstractIntlMessages> => {
  const resolvedLocale = (locale ?? defaultLocale) as Locale;
  const namespaceList =
    typeof namespaces === "string"
      ? [namespaces]
      : namespaces
        ? [...namespaces]
        : [];

  const common = await loadNamespace(resolvedLocale, "common");
  const messages: AbstractIntlMessages = { ...common };

  for (const namespace of namespaceList) {
    const pageMessages = await loadNamespace(resolvedLocale, namespace);
    if (pageMessages) {
      Object.assign(messages, pageMessages);
    }
  }

  return messages;
};

export const createGetServerSidePropsWithMessages =
  (namespace?: string | string[]): GetServerSideProps<PageProps> =>
  async (context: GetServerSidePropsContext) => ({
    props: await getI18nPageProps(context, namespace),
  });

export const getI18nPageProps = async (
  context: Pick<GetServerSidePropsContext, "locale" | "defaultLocale">,
  namespace?: string | string[]
) => {
  const locale = context.locale ?? context.defaultLocale;

  return {
    messages: await getMessages(locale, namespace),
    now: new Date().toISOString(),
  };
};
